import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useClient } from '../../context/ClientContext';

const {
  FiHome,
  FiDollarSign,
  FiShoppingBag,
  FiBriefcase,
  FiBarChart2,
  FiSettings,
  FiCreditCard,
  FiChevronDown,
  FiX,
  FiPieChart,
  FiLogOut,
  FiChevronLeft,
  FiChevronRight,
  FiCpu,
  FiShield,
  FiLink
} = FiIcons;

/*
 * ---------------------------------------------------------
 * NAVIGATION CONFIGURATION
 * ---------------------------------------------------------
 */

const navigationGroups = [
  {
    title: 'Intelligence',
    items: [
      {
        name: 'AI Copilot',
        icon: FiCpu,
        path: '/assistant'
      }
    ]
  },
  {
    title: 'Operations',
    items: [
      {
        name: 'Dashboard',
        icon: FiHome,
        path: '/dashboard'
      },
      {
        name: 'Sales',
        icon: FiDollarSign,
        hasDropdown: true,
        subItems: [
          {
            name: 'Customers',
            path: '/sales/customers'
          },
          {
            name: 'Invoices',
            path: '/sales/invoices'
          },
          {
            name: 'Payments Received',
            path: '/sales/payments-received'
          }
        ]
      },
      {
        name: 'Purchases',
        icon: FiShoppingBag,
        hasDropdown: true,
        subItems: [
          {
            name: 'Vendors',
            path: '/purchases/vendors'
          },
          {
            name: 'Bills',
            path: '/purchases/bills'
          },
          {
            name: 'Payments Made',
            path: '/purchases/payments-made'
          },
          {
            name: 'Receipts',
            path: '/receipts'
          }
        ]
      }
    ]
  },
  {
    title: 'Finance & Banking',
    items: [
      {
        name: 'Accounting',
        icon: FiBarChart2,
        hasDropdown: true,
        subItems: [
          {
            name: 'Connect Bank',
            path: '/accounting/connect-bank'
          },
          {
            name: 'Transactions',
            path: '/accounting/transactions'
          },
          {
            name: 'Reconciliation',
            path: '/accounting/reconciliation'
          },
          {
            name: 'Bank Rules',
            path: '/accounting/rules'
          },
          {
            name: 'Chart of Accounts',
            path: '/accounting/chart-of-accounts'
          }
        ]
      }
    ]
  },
  {
    title: 'Analytics',
    items: [
      {
        name: 'Reports',
        icon: FiPieChart,
        hasDropdown: true,
        subItems: [
          {
            name: 'Transaction Details',
            path: '/accounting/general-ledger'
          },
          {
            name: 'Income and Expenses',
            path: '/reports/profit-loss'
          },
          {
            name: 'Asset and Liabilities',
            path: '/reports/balance-sheet'
          },
          {
            name: 'Cash Inflow and Outflow',
            path: '/reports/cash-flow'
          },
          {
            name: 'AR Aging',
            path: '/reports/ar-aging'
          },
          {
            name: 'AP Aging',
            path: '/reports/ap-aging'
          }
        ]
      }
    ]
  },
  {
    title: 'Administration',
    items: [
      {
        name: 'Settings',
        icon: FiSettings,
        hasDropdown: true,
        subItems: [
          {
            name: 'Billing',
            icon: FiCreditCard,
            path: '/settings/billing'
          },
          {
            name: 'Payments',
            icon: FiDollarSign,
            path: '/settings/payments'
          },
          {
            name: 'Profile',
            path: '/settings/profile'
          },
          {
            name: 'Integrations',
            icon: FiLink,
            path: '/settings/integrations'
          }
        ]
      }
    ]
  }
];

const practiceNavigationGroup = {
  title: 'Client Management',
  items: [
    {
      name: 'Clients',
      icon: FiBriefcase,
      path: '/clients'
    }
  ]
};

/*
 * ---------------------------------------------------------
 * ROUTE HELPERS
 * ---------------------------------------------------------
 */

const isPathActive = (currentPath, itemPath) => {
  if (!itemPath) return false;

  return (
    currentPath === itemPath ||
    currentPath.startsWith(`${itemPath}/`)
  );
};

const isItemActive = (item, currentPath) => {
  if (isPathActive(currentPath, item.path)) {
    return true;
  }

  return Boolean(
    item.subItems?.some((subItem) =>
      isPathActive(currentPath, subItem.path)
    )
  );
};

const findActiveDropdown = (pathname) => {
  for (const group of navigationGroups) {
    for (const item of group.items) {
      if (
        item.hasDropdown &&
        item.subItems?.some((subItem) =>
          isPathActive(pathname, subItem.path)
        )
      ) {
        return item.name;
      }
    }
  }

  return null;
};

/*
 * ---------------------------------------------------------
 * DISPLAY HELPERS
 * ---------------------------------------------------------
 */

const formatRole = (role) => {
  if (!role) return 'No Firm Access';

  return String(role)
    .split('_')
    .map((word) =>
      word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(' ');
};

const formatOrganizationType = (type) => {
  if (type === 'accounting_firm') {
    return 'Accounting Firm';
  }

  if (type === 'accountant_practice') {
    return 'Accounting Practice';
  }

  return type
    ? String(type).replace(/_/g, ' ')
    : 'Firm Workspace';
};

/*
 * ---------------------------------------------------------
 * SIDEBAR
 * ---------------------------------------------------------
 */

export default function Sidebar({
  isOpen,
  onClose,
  isCollapsed,
  setIsCollapsed
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    user,
    profile,
    activeFirm,
    activeClient,
    clients,
    workspaceError,
    isLoading,
    logout
  } = useClient();

  const [openMenuName, setOpenMenuName] = useState(
    () => findActiveDropdown(location.pathname)
  );

  /*
   * -------------------------------------------------------
   * USER IDENTITY FROM SUPABASE PROFILE
   * -------------------------------------------------------
   */

  const firstName = profile?.first_name?.trim() || '';
  const lastName = profile?.last_name?.trim() || '';

  const authFullName =
    user?.user_metadata?.full_name || '';

  const fullName =
    [firstName, lastName].filter(Boolean).join(' ') ||
    authFullName ||
    user?.email?.split('@')[0] ||
    'User';

  const initials =
    [firstName?.[0], lastName?.[0]]
      .filter(Boolean)
      .join('')
      .toUpperCase() ||
    fullName.slice(0, 2).toUpperCase();

  /*
   * -------------------------------------------------------
   * VERIFIED FIRM MEMBERSHIP FROM SUPABASE
   * -------------------------------------------------------
   */

  const hasFirm = Boolean(activeFirm?.id);

  const firmName =
    activeFirm?.name || null;

  const firmRole =
    activeFirm?.role || null;

  const formattedFirmRole =
    formatRole(firmRole);

  const firmTypeLabel =
    formatOrganizationType(activeFirm?.type);

  const hasClients =
    Array.isArray(clients) && clients.length > 0;

  // Client Management is shown only for an
  // authenticated, verified managing-firm membership.
  const contextNavigationGroup = hasFirm
    ? practiceNavigationGroup
    : null;

  /*
   * -------------------------------------------------------
   * AUTOMATICALLY EXPAND ACTIVE MENU
   * -------------------------------------------------------
   */

  useEffect(() => {
    const activeDropdown = findActiveDropdown(
      location.pathname
    );

    if (activeDropdown) {
      setOpenMenuName(activeDropdown);
    }
  }, [location.pathname]);

  /*
   * -------------------------------------------------------
   * NAVIGATION HANDLERS
   * -------------------------------------------------------
   */

  const toggleMenu = (name) => {
    if (isCollapsed) {
      setIsCollapsed(false);
      setOpenMenuName(name);
      return;
    }

    setOpenMenuName((previous) =>
      previous === name ? null : name
    );
  };

  const handleNavigation = (path) => {
    if (!path) return;

    navigate(path);

    if (window.innerWidth < 768) {
      onClose?.();
    }
  };

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleLogout = async () => {
    const result = await logout();

    if (result?.success) {
      onClose?.();
      navigate('/login', { replace: true });
    }
  };

  const sidebarWidth = isCollapsed
    ? 'w-20'
    : 'w-72';

  /*
   * -------------------------------------------------------
   * RENDER NAVIGATION ITEM
   * -------------------------------------------------------
   */

  const renderNavigationItem = (item) => {
    const isMenuExpanded =
      openMenuName === item.name;

    const hasSubItems = Boolean(
      item.subItems && item.subItems.length > 0
    );

    const isActive = isItemActive(
      item,
      location.pathname
    );

    return (
      <div
        key={item.name}
        className="flex flex-col"
      >
        <button
          type="button"
          title={isCollapsed ? item.name : undefined}
          aria-label={item.name}
          aria-expanded={
            item.hasDropdown
              ? isMenuExpanded
              : undefined
          }
          aria-current={
            !item.hasDropdown && isActive
              ? 'page'
              : undefined
          }
          onClick={() =>
            item.hasDropdown
              ? toggleMenu(item.name)
              : handleNavigation(item.path)
          }
          className={`group flex w-full items-center justify-between rounded-2xl px-4 py-3 text-left transition-all duration-200 ${
            isActive && !item.hasDropdown
              ? 'bg-slate-900 text-white shadow-lg shadow-slate-200'
              : isActive && item.hasDropdown
                ? 'bg-slate-50 text-slate-900'
                : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
          }`}
        >
          <div className="flex min-w-0 items-center gap-3.5">
            <SafeIcon
              icon={item.icon}
              className={`shrink-0 text-lg transition-colors ${
                isActive
                  ? 'text-emerald-400'
                  : 'text-slate-400 group-hover:text-slate-600'
              }`}
            />

            {!isCollapsed && (
              <span
                className={`whitespace-nowrap text-[13px] font-bold tracking-tight ${
                  isActive ? 'font-black' : ''
                }`}
              >
                {item.name}
              </span>
            )}
          </div>

          {!isCollapsed && item.hasDropdown && (
            <motion.div
              animate={{
                rotate: isMenuExpanded ? 180 : 0
              }}
              transition={{ duration: 0.2 }}
            >
              <SafeIcon
                icon={FiChevronDown}
                className={`text-xs ${
                  isActive
                    ? 'text-slate-900'
                    : 'opacity-30'
                }`}
              />
            </motion.div>
          )}
        </button>

        <AnimatePresence initial={false}>
          {!isCollapsed &&
            isMenuExpanded &&
            hasSubItems && (
              <motion.div
                key={`${item.name}-submenu`}
                initial={{
                  height: 0,
                  opacity: 0
                }}
                animate={{
                  height: 'auto',
                  opacity: 1
                }}
                exit={{
                  height: 0,
                  opacity: 0
                }}
                transition={{ duration: 0.2 }}
                className="ml-6 mt-1 flex flex-col gap-0.5 overflow-hidden border-l-2 border-slate-100 py-1 pl-4"
              >
                {item.subItems.map((subItem) => {
                  const isSubActive = isPathActive(
                    location.pathname,
                    subItem.path
                  );

                  return (
                    <button
                      key={subItem.name}
                      type="button"
                      onClick={() =>
                        handleNavigation(subItem.path)
                      }
                      aria-current={
                        isSubActive ? 'page' : undefined
                      }
                      className={`flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left text-[12px] font-bold transition-all duration-200 ${
                        isSubActive
                          ? 'bg-blue-50/50 text-blue-600'
                          : 'text-slate-400 hover:bg-slate-50/50 hover:text-slate-900'
                      }`}
                    >
                      <div
                        className={`h-1 w-1 shrink-0 rounded-full ${
                          isSubActive
                            ? 'bg-blue-600'
                            : 'bg-transparent'
                        }`}
                      />

                      <span className="whitespace-nowrap">
                        {subItem.name}
                      </span>
                    </button>
                  );
                })}
              </motion.div>
            )}
        </AnimatePresence>
      </div>
    );
  };

  /*
   * -------------------------------------------------------
   * RENDER NAVIGATION GROUP
   * -------------------------------------------------------
   */

  const renderNavigationGroup = (group) => (
    <div
      key={group.title}
      className="flex flex-col gap-1"
    >
      {!isCollapsed && (
        <p className="mb-2 px-4 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
          {group.title}
        </p>
      )}

      {group.items.map(renderNavigationItem)}
    </div>
  );

  /*
   * -------------------------------------------------------
   * RENDER CLIENT MANAGEMENT
   * -------------------------------------------------------
   */

  const renderClientManagement = () => {
    if (!contextNavigationGroup) {
      return null;
    }

    return (
      <div
        key="client-management"
        className="flex flex-col gap-1"
      >
        {!isCollapsed && (
          <p className="mb-2 px-4 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
            {contextNavigationGroup.title}
          </p>
        )}

        {contextNavigationGroup.items.map(
          renderNavigationItem
        )}

        {/* First-client guidance is informational.
            Actual client creation must be authorized
            by Supabase on the backend. */}
        {!isCollapsed &&
          hasFirm &&
          firmRole === 'owner' &&
          !hasClients &&
          !workspaceError &&
          !isLoading && (
            <div className="mx-2 mt-2 rounded-xl border border-emerald-100 bg-emerald-50 p-3">
              <p className="text-xs font-bold text-emerald-900">
                No clients yet
              </p>

              <p className="mt-1 text-[11px] leading-relaxed text-emerald-700">
                Your firm is ready. Add your first client to get started.
              </p>

              <button
                type="button"
                onClick={() =>
                  handleNavigation('/clients')
                }
                className="mt-3 w-full rounded-lg bg-emerald-600 px-3 py-2 text-[11px] font-bold text-white transition-colors hover:bg-emerald-700"
              >
                Add First Client
              </button>
            </div>
          )}
      </div>
    );
  };

  /*
   * -------------------------------------------------------
   * RENDER GOVERNANCE
   * -------------------------------------------------------
   */

  const renderGovernance = () => {
    const isActive = isPathActive(
      location.pathname,
      '/audit-log'
    );

    return (
      <div
        key="governance"
        className="flex flex-col gap-1"
      >
        {!isCollapsed && (
          <p className="mb-2 px-4 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
            Governance
          </p>
        )}

        <button
          type="button"
          title={
            isCollapsed ? 'Audit Log' : undefined
          }
          onClick={() =>
            handleNavigation('/audit-log')
          }
          className={`group flex w-full items-center justify-between rounded-2xl px-4 py-3 text-left transition-all duration-200 ${
            isActive
              ? 'bg-slate-900 text-white shadow-lg shadow-slate-200'
              : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
          }`}
        >
          <div className="flex items-center gap-3.5">
            <SafeIcon
              icon={FiShield}
              className={`shrink-0 text-lg ${
                isActive
                  ? 'text-emerald-400'
                  : 'text-slate-400 group-hover:text-slate-600'
              }`}
            />

            {!isCollapsed && (
              <span className="whitespace-nowrap text-[13px] font-bold tracking-tight">
                Audit Log
              </span>
            )}
          </div>
        </button>
      </div>
    );
  };

  /*
   * -------------------------------------------------------
   * SIDEBAR CONTENT
   * -------------------------------------------------------
   */

  const sidebarContent = (
    <div
      className={`${sidebarWidth} relative flex h-full flex-col overflow-hidden border-r border-slate-200 bg-white shadow-sm transition-all duration-300`}
    >
      {/* LOGO */}
      <div className="relative flex h-24 shrink-0 items-center justify-center border-b border-slate-50 px-6">
        <button
          type="button"
          onClick={() =>
            handleNavigation('/')
          }
          aria-label="Go to home"
          className="group flex items-center justify-center"
        >
          <img
            src={
              isCollapsed
                ? 'https://media-manager-c.questera.ai/greta-media/46a9e1323b5c997de8c3ebac6eb3f9d74ccd00e463a5744676fe05afae233bf74d2b422fbe3649d2b4b7a9fc91fa3904/images/aW1hZ2UvcG5n/26b5741299d48194c5b9b354a9d8f41d.png'
                : 'https://media-manager-c.questera.ai/greta-media/46a9e1323b5c997de8c3ebac6eb3f9d74ccd00e463a5744676fe05afae233bf74d2b422fbe3649d2b4b7a9fc91fa3904/images/aW1hZ2UvcG5n/87c579e01acaf7cc7d05a8aca31a1291.png'
            }
            className={
              isCollapsed
                ? 'h-11 w-11 object-contain'
                : 'h-auto w-full max-w-[190px] object-contain'
            }
            alt="ProfitYO Logo"
          />
        </button>

        <button
          type="button"
          onClick={onClose}
          aria-label="Close sidebar"
          className="absolute right-4 rounded-full p-2 text-slate-400 hover:bg-slate-100 md:hidden"
        >
          <SafeIcon
            icon={FiX}
            className="text-xl"
          />
        </button>
      </div>

      {/* NAVIGATION */}
      <nav className="sidebar-scrollbar flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 py-6">

        {/* INTELLIGENCE */}
        {renderNavigationGroup(navigationGroups[0])}

        {/* CLIENT MANAGEMENT */}
        {renderClientManagement()}

        {/* OPERATIONS, FINANCE, REPORTS, SETTINGS */}
        {navigationGroups
          .slice(1)
          .map(renderNavigationGroup)}

        {/* GOVERNANCE */}
        {renderGovernance()}
      </nav>

      {/* USER PROFILE / COLLAPSE */}
      <div className="border-t border-slate-100 bg-slate-50/30 p-4">

        {!isCollapsed && (
          <div className="mb-4 rounded-[24px] border border-slate-200/60 bg-white p-4 shadow-sm">

            {/* FIRM WORKSPACE */}
            <div className="mb-4 border-b border-slate-100 pb-3">
              <p className="mb-1 text-[10px] font-black uppercase tracking-widest text-slate-400">
                Firm Workspace
              </p>

              {isLoading ? (
                <p className="text-xs font-semibold text-slate-400">
                  Loading workspace...
                </p>
              ) : hasFirm ? (
                <>
                  <p
                    title={firmName}
                    className="truncate text-[13px] font-black text-slate-900"
                  >
                    {firmName}
                  </p>

                  <p className="mt-1 text-[11px] font-semibold text-slate-500">
                    {firmTypeLabel}
                  </p>

                  <p className="mt-1 text-[10px] font-black uppercase tracking-widest text-emerald-700">
                    {formattedFirmRole}
                  </p>
                </>
              ) : (
                <p className="text-xs font-semibold text-slate-500">
                  {workspaceError
                    ? 'Workspace unavailable'
                    : 'No firm membership'}
                </p>
              )}
            </div>

            {/* USER */}
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-sm font-black text-white shadow-md">
                {initials}
              </div>

              <div className="min-w-0 flex-1">
                <p
                  title={fullName}
                  className="truncate text-[13px] font-black text-slate-900"
                >
                  {fullName}
                </p>

                <p
                  title={user?.email || ''}
                  className="mt-1 truncate text-[10px] font-medium text-slate-400"
                >
                  {user?.email || ''}
                </p>
              </div>

              <button
                type="button"
                onClick={handleLogout}
                aria-label="Log out"
                title="Log out"
                className="p-2 text-slate-300 transition-colors hover:text-rose-500"
              >
                <SafeIcon icon={FiLogOut} />
              </button>
            </div>

            {/* ACTIVE CLIENT */}
            {activeClient && (
              <div className="mt-3 rounded-xl bg-slate-50 px-3 py-2">
                <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
                  Active Client
                </p>

                <p className="mt-1 truncate text-xs font-bold text-slate-700">
                  {activeClient.name}
                </p>
              </div>
            )}

          </div>
        )}

        {/* COLLAPSE CONTROL */}
        <button
          type="button"
          onClick={handleToggleCollapse}
          aria-label={
            isCollapsed
              ? 'Expand sidebar'
              : 'Collapse sidebar'
          }
          className="flex w-full items-center justify-center rounded-2xl border border-slate-200 bg-white py-3 text-slate-400 shadow-sm transition-all hover:bg-slate-50 hover:text-slate-900 active:scale-95"
        >
          <SafeIcon
            icon={
              isCollapsed
                ? FiChevronRight
                : FiChevronLeft
            }
            className="text-xl"
          />

          {!isCollapsed && (
            <span className="ml-2 text-[10px] font-black uppercase tracking-[0.2em]">
              Collapse View
            </span>
          )}
        </button>

        {/* COLLAPSED LOGOUT */}
        {isCollapsed && (
          <button
            type="button"
            onClick={handleLogout}
            aria-label="Log out"
            title="Log out"
            className="mt-2 flex w-full items-center justify-center rounded-2xl border border-slate-200 bg-white py-3 text-slate-400 transition-all hover:bg-rose-50 hover:text-rose-500"
          >
            <SafeIcon
              icon={FiLogOut}
              className="text-lg"
            />
          </button>
        )}
      </div>
    </div>
  );

  /*
   * -------------------------------------------------------
   * DESKTOP / MOBILE
   * -------------------------------------------------------
   */

  return (
    <>
      {/* DESKTOP */}
      <aside className="fixed bottom-0 left-0 top-0 z-30 hidden flex-col overflow-hidden transition-all duration-300 md:flex">
        {sidebarContent}
      </aside>

      {/* MOBILE */}
      <AnimatePresence>
        {isOpen && (
          <div className="md:hidden">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-md"
            />

            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ duration: 0.25 }}
              className="fixed inset-y-0 left-0 z-50 overflow-hidden"
            >
              {sidebarContent}
            </motion.aside>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}